import { ReactNode } from "react";
import { Attachment } from "@carbon/icons-react";
import { Tag } from "@carbon/react";
import {
  DataTableColumn,
  DataTableRow,
} from "../../components/carbon/DataTable/DataTable";
import { OfferResponse, SupplierRfqActivity } from "../../types";
import SupplierValidationTag, { activityByEmail } from "./SupplierValidationTag";

type TFunc = (key: string) => string;

export interface MultipleOfferRow extends DataTableRow {
  id: string;
  offers: OfferResponse[];
  supplier: ReactNode;
  validation: ReactNode;
  price: ReactNode;
  comment: ReactNode;
  attachment: ReactNode;
  status: ReactNode;
}

/**
 * One row per seller on a JOINT order: `offers` is that seller's full set of offers, one per
 * material line. Faithful port of the pre-Carbon `OffersOnMultipleOrders.tsx` + `OfferItem.tsx`
 * pairing, which — despite `offers` carrying every material line — only ever displayed the FIRST
 * offer's (`offers[0]`) price/comment/attachment, using the rest of the array solely to compute
 * the "awarded" badge (`offers.some(o => o.selected)`). That is preserved as-is here rather than
 * "fixed" into a per-material breakdown, since this task is a Carbon rebuild, not a behaviour
 * change.
 */
export const buildMultipleOfferRows = (
  offersBySeller: OfferResponse[][] | undefined,
  hideOfferDetails: boolean,
  t: TFunc,
  activity?: SupplierRfqActivity[]
): MultipleOfferRow[] => {
  const activityOf = activityByEmail(activity);
  if (!offersBySeller) return [];

  return offersBySeller
    .filter((offers) => offers?.[0])
    .map((offers) => {
      const first = offers[0];
      const isSelected = offers.some((offer) => offer.selected);
      return {
        id: first.id,
        offers,
        supplier: first.user?.company?.name || "-",
        validation: (
          <SupplierValidationTag
            activity={activityOf.get(first.user?.email?.toLowerCase() ?? "")}
          />
        ),
        price: hideOfferDetails
          ? null
          : `${first.price} ${first.currency} / ${first.order?.measurementUnit ?? ""}`,
        comment: hideOfferDetails ? null : first.comment || null,
        attachment:
          !hideOfferDetails && first.fileURL ? (
            <Attachment aria-label={t("common.viewFile")} />
          ) : null,
        status:
          !hideOfferDetails && isSelected ? (
            <Tag type="green">{t("offer.success")}</Tag>
          ) : null,
      };
    });
};

export const multipleOfferListHeaders = (
  t: TFunc
): DataTableColumn<MultipleOfferRow>[] => [
  { key: "supplier", header: t("tenders.data.suppliers") },
  { key: "validation", header: t("tenders.supplierActivity.validation") },
  { key: "price", header: t("tenders.form.price") },
  { key: "comment", header: t("common.comment") },
  { key: "attachment", header: t("tenders.data.attachment") },
  { key: "status", header: t("tenders.data.offerStatus") },
];
